import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  getIndustrialRiskPurchaseEnvelope,
  getIndustrialRiskSuppliers,
  simulateHistoricalV2,
} from '../services/industrialRiskService';

import { formatEsNumber } from '../../industrialCost/utils/numberFormat';
type SupplierOption = {
  supplier: string;
  avg_price_weighted_eur_per_kg?: number;
  last_price_eur_per_kg?: number;
  n_lots?: number;
  total_kg?: number;
};

function n(x: any): number | undefined {
  return typeof x === 'number' && Number.isFinite(x) ? x : undefined;
}

function pct01(x: number): string {
  return (x * 100).toFixed(0);
}

function eurKg(x: number): string {
  return x.toFixed(2);
}

function extractSuppliers(raw: any): SupplierOption[] {
  const arr = Array.isArray(raw?.suppliers) ? raw.suppliers : [];
  return arr
    .map((r: any) => ({
      supplier: String(r?.supplier ?? ''),
      avg_price_weighted_eur_per_kg: n(r?.avg_price_weighted_eur_per_kg),
      last_price_eur_per_kg: n(r?.last_price_eur_per_kg),
      n_lots: n(r?.n_lots),
      total_kg: n(r?.total_kg),
    }))
    .filter((s: SupplierOption) => s.supplier);
}

type EnvelopeExtract = {
  quality?: { decision_grade?: boolean; reasons?: string[] };
  baseline_price?: number;

  yieldMean?: number;
  yieldP10?: number;
  yieldP90?: number;

  mixTop?: Array<{ format_code: string; share_units?: number }>;

  ladder?: Array<{
    risk_target: number;
    max_price?: number;
    risk_at_baseline_price?: number;
  }>;
  // Dinero al baseline (€/kg y ejemplo en € totales)
  baselineProfitMean?: number;
  baselineProfitP10?: number;
  baselineProbLoss?: number;

  baselineTargetQtyKg?: number;
  baselineProfitTotalMean?: number;
  baselineProfitTotalP10?: number;

  raw?: any;
};

function extractEnvelope(raw: any): EnvelopeExtract {
  const qr = raw?.quality_report;
  const quality = qr
    ? {
        decision_grade: Boolean(qr.decision_grade),
        reasons: Array.isArray(qr.reasons) ? qr.reasons.map((x: any) => String(x)) : [],
      }
    : undefined;

  const y = raw?.industrial_summary?.yield;
  const mix = Array.isArray(raw?.industrial_summary?.mix_top_formats_by_units)
    ? raw.industrial_summary.mix_top_formats_by_units
    : [];

  const ladderRaw = Array.isArray(raw?.price_envelope?.ladder) ? raw.price_envelope.ladder : [];

  // Tomamos el primer escalón como referencia para “baseline price”
  const simBase = ladderRaw?.[0]?.simulation_at_baseline_price;

  const ppi = simBase?.profit_per_kg_input;
  const ptt = simBase?.profit_total_target;

  const baselineProfitMean = n(ppi?.mean);
  const baselineProfitP10 = n(ppi?.p10);
  const baselineProbLoss = n(ppi?.probability_of_loss);

  const baselineTargetQtyKg = n(ptt?.target_quantity_in_kg);
  const baselineProfitTotalMean = n(ptt?.mean);
  const baselineProfitTotalP10 = n(ptt?.p10);

  const ladder = ladderRaw
    .map((r: any) => ({
      risk_target: n(r?.risk_target) ?? 0.1,
      max_price: n(r?.max_price_eur_per_kg),
      risk_at_baseline_price: n(r?.risk_at_baseline_price),
    }))
    .filter((x: any) => typeof x.risk_target === 'number');

  return {
    quality,
    baseline_price: n(raw?.baseline_price_eur_per_kg),
    yieldMean: n(y?.mean),
    yieldP10: n(y?.p10),
    yieldP90: n(y?.p90),
    mixTop: mix
      .map((m: any) => ({
        format_code: String(m?.format_code ?? ''),
        share_units: n(m?.share_units),
      }))
      .filter((m: any) => m.format_code),
    baselineProfitMean,
    baselineProfitP10,
    baselineProbLoss,
    baselineTargetQtyKg,
    baselineProfitTotalMean,
    baselineProfitTotalP10,
    ladder,
    raw,
  };
}

type OfferKpis = {
  offered_mean?: number;
  offered_p10?: number;
  risk_at_min?: number;
  max_price?: number;
};

function extractOfferKpis(resp: any): OfferKpis {
  const sim = resp?.simulation_at_offered_price;

  const mean =
    n(sim?.profit_per_kg_input?.mean) ??
    n(sim?.profit_per_kg_input_mean) ??
    n(sim?.summary?.mean) ??
    undefined;

  const p10 =
    n(sim?.profit_per_kg_input?.p10) ??
    n(sim?.profit_per_kg_input_p10) ??
    n(sim?.summary?.p10) ??
    undefined;

  return {
    offered_mean: mean,
    offered_p10: p10,
    risk_at_min: n(resp?.risk_at_min),
    max_price: n(resp?.max_price_eur_per_kg),
  };
}

export default function DecisionView() {
  // Suppliers
  const [suppliersRaw, setSuppliersRaw] = useState<unknown>(null);
  const suppliers = useMemo(() => extractSuppliers(suppliersRaw as any), [suppliersRaw]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);

  // Selection
  const [supplier, setSupplier] = useState<string>('');

  // Envelope
  const [envelopeRaw, setEnvelopeRaw] = useState<unknown>(null);
  const envelope = useMemo(() => extractEnvelope(envelopeRaw as any), [envelopeRaw]);
  const [loadingEnvelope, setLoadingEnvelope] = useState(false);
  const [envelopeError, setEnvelopeError] = useState<string | null>(null);

  // Advanced: manual offer
  const [offeredPrice, setOfferedPrice] = useState<string>('0.95');
  const [riskTarget, setRiskTarget] = useState<string>('0.10');
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [resultRaw, setResultRaw] = useState<unknown>(null);
  const offerKpis = useMemo(() => extractOfferKpis(resultRaw as any), [resultRaw]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoadingSuppliers(true);
      setSupplierError(null);
      try {
        const data = await getIndustrialRiskSuppliers();
        if (!cancelled) setSuppliersRaw(data);
      } catch (e: any) {
        if (!cancelled) setSupplierError(e?.message ?? 'No se pudieron cargar proveedores.');
      } finally {
        if (!cancelled) setLoadingSuppliers(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSupplierMeta = useMemo(
    () => suppliers.find((s) => s.supplier === supplier) ?? null,
    [suppliers, supplier]
  );

  // Load envelope when supplier changes
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setEnvelopeError(null);
      setEnvelopeRaw(null);

      if (!supplier) return;

      setLoadingEnvelope(true);
      try {
        const data = await getIndustrialRiskPurchaseEnvelope({
          supplier,
          baseline: 'weighted_avg',
          risk_targets: [0.05, 0.1, 0.2],
          n_sims: 3000,
          random_seed: 7,
        });
        if (!cancelled) setEnvelopeRaw(data);
      } catch (e: any) {
        if (!cancelled) setEnvelopeError(e?.message ?? 'No se pudo calcular el sobre de compra.');
      } finally {
        if (!cancelled) setLoadingEnvelope(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [supplier]);

  const offered = Number(offeredPrice);
  const target = Number(riskTarget);
  const offeredValid = Number.isFinite(offered) && offered > 0;
  const targetValid = Number.isFinite(target) && target > 0 && target < 1;

  const onEvaluate = async () => {
    setRunError(null);
    setResultRaw(null);

    if (!supplier) {
      setRunError('Selecciona un proveedor.');
      return;
    }
    if (!offeredValid) {
      setRunError('Introduce un precio ofertado válido (€/kg).');
      return;
    }
    if (!targetValid) {
      setRunError('Introduce un objetivo de riesgo válido (por ejemplo 0.10 para 10%).');
      return;
    }

    setRunLoading(true);
    try {
      const payload = {
        mode: 'purchase_risk' as const,
        supplier,
        risk_target: target,
        price_min: offered,
        n_sims: 4000,
        random_seed: 7,
      };

      const resp = await simulateHistoricalV2(payload);
      setResultRaw(resp);
    } catch (e: any) {
      setRunError(e?.message ?? 'Error evaluando la oferta.');
    } finally {
      setRunLoading(false);
    }
  };

  return (
    <Stack spacing={2.2}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Compra por proveedor
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Selecciona un proveedor y verás directamente cuánto puedes pagar para distintos niveles de control del riesgo.
        </Typography>
      </Box>

      {supplierError ? <Alert severity="error">{supplierError}</Alert> : null}

      {/* Selector único (camino principal) */}
      <Box
        sx={{
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.10)',
          bgcolor: 'rgba(255,255,255,0.03)',
        }}
      >
        <Grid container spacing={1.2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" disabled={loadingSuppliers}>
              <InputLabel>Proveedor</InputLabel>
              <Select value={supplier} label="Proveedor" onChange={(e) => setSupplier(String(e.target.value))}>
                {suppliers.map((s) => (
                  <MenuItem key={s.supplier} value={s.supplier}>
                    {s.supplier}
                    {s.avg_price_weighted_eur_per_kg != null ? ` · base ${eurKg(s.avg_price_weighted_eur_per_kg)} €/kg` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {loadingSuppliers ? 'Cargando proveedores…' : suppliers.length ? `${suppliers.length} proveedores disponibles` : '—'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Último precio
                </Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  {selectedSupplierMeta?.last_price_eur_per_kg != null ? `${eurKg(selectedSupplierMeta.last_price_eur_per_kg)} €/kg` : '—'}
                </Typography>
              </Box>

              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Baseline histórico
                </Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  {selectedSupplierMeta?.avg_price_weighted_eur_per_kg != null ? `${eurKg(selectedSupplierMeta.avg_price_weighted_eur_per_kg)} €/kg` : '—'}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {envelopeError ? <Alert severity="error">{envelopeError}</Alert> : null}
      {loadingEnvelope ? (
        <Alert severity="info" icon={<CircularProgress size={16} />}>
          Calculando sobre de compra…
        </Alert>
      ) : null}

      {envelope.quality?.decision_grade === false ? (
        <Alert severity="warning">
          <Typography sx={{ fontWeight: 900 }}>REVISAR_DATOS</Typography>
          {envelope.quality.reasons?.length ? (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {envelope.quality.reasons.slice(0, 3).map((r, idx) => (
                <Box component="li" key={`${idx}-${r}`}>
                  <Typography variant="body2">{r}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2">Los datos no son suficientes para una recomendación robusta.</Typography>
          )}
        </Alert>
      ) : null}

      {/* Impacto económico al precio actual (baseline) */}
      {supplier ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.10)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              Impacto económico al precio actual
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Traducción directa a margen (€/kg) y a euros totales para un volumen histórico típico.
            </Typography>

            <Grid container spacing={1.2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                    Beneficio esperado
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {envelope.baselineProfitMean != null ? `${eurKg(envelope.baselineProfitMean)} €/kg` : '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Con el precio baseline del proveedor.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                    “Mes malo” típico
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {envelope.baselineProfitP10 != null ? `${eurKg(envelope.baselineProfitP10)} €/kg` : '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Escenario adverso razonable (p10).
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                    Riesgo de pérdida
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {envelope.baselineProbLoss != null ? `${pct01(envelope.baselineProbLoss)}%` : '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Probabilidad de margen &lt; 0.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                Ejemplo en euros (volumen típico histórico)
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Volumen usado: {envelope.baselineTargetQtyKg != null ? `${formatEsNumber(Math.round(envelope.baselineTargetQtyKg))} kg` : '—'}.
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Beneficio esperado: {envelope.baselineProfitTotalMean != null ? `${formatEsNumber(Math.round(envelope.baselineProfitTotalMean))} €` : '—'} ·
                “Mes malo” (p10): {envelope.baselineProfitTotalP10 != null ? `${formatEsNumber(Math.round(envelope.baselineProfitTotalP10))} €` : '—'}.
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Nota: este bloque usa un volumen “típico” histórico (default del motor). No es contabilidad; es estimación bajo variabilidad real.
              </Typography>
            </Box>
          </Stack>
        </Box>
      ) : null}

      {/* Sobre de compra */}
      {supplier ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.10)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Límite de compra por nivel de riesgo
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  “Precio máximo recomendable” para mantener el riesgo de perder dinero por debajo del nivel indicado.
                </Typography>
              </Box>

              <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Baseline usado
                </Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  {envelope.baseline_price != null ? `${eurKg(envelope.baseline_price)} €/kg` : '—'}
                </Typography>
              </Box>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Nivel de control</TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="right">
                    Precio máximo recomendable (€/kg)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 900 }} align="right">
                    Riesgo al baseline (si aplica)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(envelope.ladder ?? []).map((r) => (
                  <TableRow key={r.risk_target}>
                    <TableCell>Riesgo ≤ {pct01(r.risk_target)}%</TableCell>
                    <TableCell align="right">{r.max_price != null ? eurKg(r.max_price) : '—'}</TableCell>
                    <TableCell align="right">{r.risk_at_baseline_price != null ? `${pct01(r.risk_at_baseline_price)}%` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Interpretación: si el proveedor es estable, el “precio máximo” cambia poco entre 5/10/20%. Si cambia mucho, el proveedor es más incierto.
            </Typography>
          </Stack>
        </Box>
      ) : null}

      {/* Resumen industrial */}
      {supplier ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.10)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              Resumen industrial del proveedor
            </Typography>
            <Grid container spacing={1.2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                    Rendimiento (yield) típico
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {envelope.yieldMean != null ? `${(envelope.yieldMean * 100).toFixed(1)}%` : '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Rango habitual: {envelope.yieldP10 != null ? `${(envelope.yieldP10 * 100).toFixed(1)}%` : '—'} a{' '}
                    {envelope.yieldP90 != null ? `${(envelope.yieldP90 * 100).toFixed(1)}%` : '—'}.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                    Mix probable (formatos más frecuentes)
                  </Typography>
                  {(envelope.mixTop?.length ?? 0) ? (
                    <Stack spacing={0.4}>
                      {envelope.mixTop!.slice(0, 5).map((m) => (
                        <Stack key={m.format_code} direction="row" justifyContent="space-between">
                          <Typography variant="body2">{m.format_code}</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.85 }}>
                            {m.share_units != null ? `${(m.share_units * 100).toFixed(0)}%` : '—'}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      —
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      ) : null}

      {/* Modo avanzado (opcional): evaluar oferta concreta */}
      <Accordion sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 900 }}>Modo avanzado: evaluar una oferta concreta</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
            Si tienes un precio ofertado específico, puedes evaluarlo directamente (probabilidad de perder dinero y precio máximo recomendado).
          </Typography>

          <Grid container spacing={1.2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Precio ofertado (€/kg)"
                value={offeredPrice}
                onChange={(e) => setOfferedPrice(e.target.value)}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Riesgo objetivo"
                value={riskTarget}
                onChange={(e) => setRiskTarget(e.target.value)}
                helperText="0.10 = 10%"
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={onEvaluate}
                disabled={runLoading || loadingSuppliers}
                sx={{ height: '40px', fontWeight: 900 }}
              >
                {runLoading ? <CircularProgress size={18} /> : 'Evaluar'}
              </Button>
            </Grid>
          </Grid>

          {runError ? <Alert severity="error" sx={{ mt: 1.2 }}>{runError}</Alert> : null}

          <Grid container spacing={1.2} sx={{ mt: 0.4 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                  Margen esperado (oferta)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {offerKpis.offered_mean != null ? `${eurKg(offerKpis.offered_mean)} €/kg` : '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                  “Mes malo” típico (p10)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {offerKpis.offered_p10 != null ? `${eurKg(offerKpis.offered_p10)} €/kg` : '—'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1.6, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.6 }}>
                  Riesgo de perder dinero
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {offerKpis.risk_at_min != null ? `${pct01(offerKpis.risk_at_min)}%` : '—'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Accordion sx={{ mt: 1.2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 900 }}>Detalle técnico (opcional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.2,
                  borderRadius: 2,
                  bgcolor: 'rgba(0,0,0,0.35)',
                  overflow: 'auto',
                  maxHeight: 420,
                  fontSize: 12,
                }}
              >
                {resultRaw ? JSON.stringify(resultRaw, null, 2) : '—'}
              </Box>
            </AccordionDetails>
          </Accordion>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}